import {
  BedrockRuntimeClient,
  ConverseCommand,
} from "@aws-sdk/client-bedrock-runtime"
import {
  Document,
  isRichTextContent,
  isTextContent,
  isUIDContent,
  isWidgetContent,
  traverseDocument,
} from "@prismicio/types-internal/lib/content"
import type { StaticCustomType } from "@prismicio/types-internal/lib/customtypes"
import { z } from "zod"

export const metaTitleProperty = "meta_title"
export const metaDescriptionProperty = "meta_description"
export type MetadataType =
  | typeof metaTitleProperty
  | typeof metaDescriptionProperty

interface GenerateSEOMetadataArgs {
  pageTextContent: string
  contentLocale: string
  config: { accessKeyId: string; secretAccessKey: string; region: string }
}

const generateSEOMetadataSchema = z.object({
  [metaTitleProperty]: z.string(),
  [metaDescriptionProperty]: z.string(),
})

export async function generateSEOMetadata(args: GenerateSEOMetadataArgs) {
  const { pageTextContent, contentLocale, config } = args

  const client = new BedrockRuntimeClient({
    region: config.region,
    credentials: {
      accessKeyId: config.accessKeyId,
      secretAccessKey: config.secretAccessKey,
    },
  })

  try {
    const command = new ConverseCommand({
      modelId: "anthropic.claude-3-5-sonnet-20240620-v1:0",
      system: [{ text: generateSystemPrompt(contentLocale) }],
      messages: [
        {
          role: "user",
          content: [{ text: `Input:\n${pageTextContent}` }],
        },
      ],
    })

    const response = await client.send(command)

    return generateSEOMetadataSchema.parse(
      JSON.parse(response.output?.message?.content?.[0]?.text || ""),
    )
  } catch (error) {
    console.error("Failed to generate SEO metadata", error)
    throw error
  }
}

export const metaTitleConfig = { min: 50, max: 60 } as const
export const metaDescriptionConfig = { min: 120, max: 165 } as const

function generateSystemPrompt(contentLocale: string = "en-us") {
  return `
    You are an expert SEO specialist specializing in mobile-first optimization. Your task is to analyze the provided website content and generate a meta title and meta description optimized for mobile SEO. Follow these steps precisely:

    1. Language Context:
    The language of the content is provided as ${contentLocale}. If ${contentLocale} is invalid, missing, or cannot be used, identify the language of the content directly and write the meta information in that language. Do not explicitly mention the language in the output.

    2. Content Analysis:
    Examine the input text to identify the main topic, primary keywords, and core intent of the content. Focus on the most SEO-relevant sections, typically in headings, titles, and introductory paragraphs.
      - If the content is comparing two or more entities (e.g., "Prismic vs Contentful" or "Product A vs Product B"), ensure the meta title and description reflect both the comparison aspect and the conclusion (if any).
      - The meta title and description should highlight the core subject of the comparison (e.g., comparing CMS tools) while also acknowledging any key takeaways or recommendations (e.g., the superiority of one tool over the other).
      -For comparison content, do not focus solely on one of the entities or the conclusion—balance both the topic of the comparison and the outcome to provide clarity on what the page is addressing.  

    3.Meta Title Creation:
      - Write a concise, compelling meta title, it must be near ${metaTitleConfig.min} characters. (never exceed this limit)
      - Place the most important keyword(s) at the beginning.
      - Ensure it is accurate, engaging, and click-worthy.

    4. Meta Description Creation:
      - Write a clear, informative meta description, it must be near ${metaDescriptionConfig.min} characters. (never exceed this limit)
      - Prioritize key information in the first sentence.
      - Include primary keywords naturally, summarize the content, and use action-oriented language.
    
    5. Best Practices:
      - Avoid keyword stuffing and write in a natural, user-friendly tone.
      - Adapt the meta information to align with language-specific nuances and SEO conventions.
      - Ensure both the meta title and description are unique, relevant, and aligned with the page’s content.
      - Do not reference or use any existing meta titles or descriptions already in the content. Be creative and generate unique meta information.
    
    6. Output Requirements:
      - Return the results in JSON format with only two keys: "${metaTitleProperty}" and "${metaDescriptionProperty}".
      - Do not include any additional text or explanations.
  `
}

export function getPageTextContent(
  document: Document,
  customType: StaticCustomType,
) {
  const listOfTextFields: { label: string; value: string }[] = []

  traverseDocument({
    document,
    customType,
  })({
    transformWidget: ({ content, model, apiId }) => {
      if (apiId === metaTitleProperty || apiId === metaDescriptionProperty)
        return undefined

      if (
        (isRichTextContent(content) && model?.type === "StructuredText") ||
        (isTextContent(content) && model?.type === "Text") ||
        (isUIDContent(content) && model?.type === "UID")
      ) {
        const value = extractTextFromField(content)

        if (value) {
          listOfTextFields.push({
            label: model.config?.label || "",
            value: value,
          })
        }
      }
      return undefined
    },
  })

  const pageContent = listOfTextFields
    .map((field) => JSON.stringify(field))
    .join("\n")

  return pageContent
}

export function extractTextFromField(
  contentField: unknown,
): string | undefined {
  if (!isWidgetContent(contentField)) return undefined

  if (contentField.__TYPE__ === "UIDContent") {
    return contentField.value
  }

  if (contentField.__TYPE__ === "StructuredTextContent") {
    const [firstValue] = contentField.value
    if (
      !firstValue ||
      firstValue.type === "image" ||
      firstValue.type === "embed"
    ) {
      return undefined
    }

    return firstValue.content.text
  }

  if (
    contentField.__TYPE__ === "FieldContent" &&
    contentField.type === "Text"
  ) {
    return contentField.value
  }

  return undefined
}

export function isPageContentRelevantForAISeo(pageContent: string): boolean {
  return pageContent.length >= 300
}
